import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import { Target, ArrowRight, TrendUp, CurrencyCircleDollar, UserPlus, GraduationCap, GlobeHemisphereEast, Lightning, Package } from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = {
  'Financial': CurrencyCircleDollar,
  'Customer Acquisition': UserPlus,
  'Learning & Development': GraduationCap,
  'Market Expansion': GlobeHemisphereEast,
  'Operational Excellence': Lightning,
  'Product Mix': Package,
};

const COLORS = {
  'Financial': '#10B981',
  'Customer Acquisition': '#3B82F6',
  'Learning & Development': '#8B5CF6',
  'Market Expansion': '#F59E0B',
  'Operational Excellence': '#06B6D4',
  'Product Mix': '#EC4899',
};

const formatValue = (val, unit) => {
  if (unit === 'INR') {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)} L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
    return `₹${Math.round(val)}`;
  }
  return `${Math.round(val * 100) / 100}${unit ? ' ' + unit : ''}`;
};

const periodLabel = (type, start) => {
  const s = new Date(start);
  if (type === 'year') return `Year ${s.getFullYear()}`;
  if (type === 'quarter') return `Q${Math.floor(s.getMonth() / 3) + 1} ${s.getFullYear()}`;
  if (type === 'month') return s.toLocaleString('en', { month: 'short', year: 'numeric' });
  return `Custom`;
};

const MyGoalsWidget = () => {
  const { user } = useAuthStore();
  const tp = useTenantPath();
  const [goals, setGoals] = useState([]);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/goals`),
      axios.get(`${API}/goals/performance`),
    ]).then(([gRes, pRes]) => {
      const all = gRes.data.goals || [];
      const mine = user ? all.filter(g => g.assigned_to === user.id) : [];
      const active = mine.filter(g => g.status === 'active')
        .sort((a, b) => (b.credit_weight || 0) - (a.credit_weight || 0))
        .slice(0, 3);
      setGoals(active);
      setPerf(pRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (loading || !perf) return null;
  // Hide if user has no goals at all
  if ((perf.total_goals || 0) === 0) return null;

  return (
    <div className="medical-card p-5 mb-6" data-testid="my-goals-widget">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#E2F3F0] text-[#00a69c]">
            <Target size={18} weight="bold" />
          </div>
          <div>
            <h3 className="font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>My Goals</h3>
            <p className="text-xs text-[#6D8E87]">Top active targets · updated live</p>
          </div>
        </div>
        <Link to={tp("/goals")} className="text-sm text-[#00a69c] font-medium hover:underline flex items-center gap-1" data-testid="widget-view-all-goals">
          View all <ArrowRight size={14} weight="bold" />
        </Link>
      </div>

      {/* Performance summary strip */}
      <div className="grid grid-cols-4 gap-3 mb-4 pb-4 border-b border-[#F4F9F7]">
        <div>
          <div className="text-xs uppercase text-[#6D8E87] font-semibold tracking-wide mb-1 flex items-center gap-1">
            <TrendUp size={12} className="text-[#00a69c]" /> Score
          </div>
          <div className="text-2xl font-bold text-[#00a69c]" style={{ fontFamily: 'Outfit' }} data-testid="widget-score">{perf.score}%</div>
        </div>
        <div>
          <div className="text-xs uppercase text-[#6D8E87] font-semibold tracking-wide mb-1">Active</div>
          <div className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Outfit' }}>{perf.active_goals}</div>
        </div>
        <div>
          <div className="text-xs uppercase text-[#6D8E87] font-semibold tracking-wide mb-1">Done</div>
          <div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit' }}>{perf.completed_goals}</div>
        </div>
        <div>
          <div className="text-xs uppercase text-[#6D8E87] font-semibold tracking-wide mb-1">Missed</div>
          <div className="text-2xl font-bold text-red-500" style={{ fontFamily: 'Outfit' }}>{perf.missed_goals}</div>
        </div>
      </div>

      {/* Top 3 active goals */}
      {goals.length === 0 ? (
        <div className="text-sm text-[#6D8E87] py-2">No active goals right now. Great job staying on top!</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {goals.map(g => {
            const Icon = ICONS[g.category] || Target;
            const color = COLORS[g.category] || '#00a69c';
            return (
              <Link to={tp("/goals")} key={g.id} className="block p-3 rounded-lg border border-[#F4F9F7] hover:border-[#D5E8E4] hover:bg-[#FAFFFE] transition" data-testid={`widget-goal-${g.id}`}>
                <div className="flex items-start gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}20`, color }}>
                    <Icon size={16} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#052A23] truncate">{g.title}</div>
                    <div className="text-xs text-[#6D8E87]">{periodLabel(g.period_type, g.period_start)} · {g.credit_weight} credits</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-[#6D8E87]">
                    {formatValue(g.current_value || 0, g.unit)} / <span className="font-semibold text-[#052A23]">{formatValue(g.target_value, g.unit)}</span>
                  </span>
                  <span className="font-bold" style={{ color }}>{g.progress_percent}%</span>
                </div>
                <div className="h-1.5 bg-[#F4F9F7] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, g.progress_percent)}%`, backgroundColor: color }} />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyGoalsWidget;
